import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faPlus, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface Vendor {
    id: number; name: string; service_provided: string; processes_phi: boolean;
    agreement_status: string; risk_rating: string;
    agreement_signed_date: string | null; agreement_expiry_date: string | null;
    contact_email: string; notes: string;
}

const AGREEMENT_COLORS: Record<string, string> = {
    none: 'danger', pending: 'warning', signed: 'success', expired: 'danger',
};
const RISK_COLORS: Record<string, string> = { low: 'secondary', medium: 'info', high: 'warning' };
const AGREEMENT_STATUSES = ['none', 'pending', 'signed', 'expired'];

const VendorRegister: React.FC = () => {
    const { api } = useInternalApi();
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({
        name: '', service_provided: '', processes_phi: false,
        agreement_status: 'none', risk_rating: 'medium', contact_email: '',
    });

    useEffect(() => { document.title = 'Vendor / BAA Register - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('compliance-api/vendors/');
            if (res.data?.status === 'success') setVendors(res.data.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        if (!form.name.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Vendor name is required.' });
            return;
        }
        try {
            await api.post('compliance-api/vendors/', form);
            setShowCreate(false);
            setForm({ name: '', service_provided: '', processes_phi: false, agreement_status: 'none', risk_rating: 'medium', contact_email: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not register vendor.' });
        }
    };

    const patchVendor = async (v: Vendor, payload: any) => {
        try {
            await api.patch(`compliance-api/vendors/${v.id}/`, payload);
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update vendor.' });
        }
    };

    const removeVendor = async (v: Vendor) => {
        const confirm = await Swal.fire({ title: `Remove ${v.name}?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`compliance-api/vendors/${v.id}/`);
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not remove vendor.' });
        }
    };

    const unprotected = vendors.filter(v => v.processes_phi && v.agreement_status !== 'signed').length;

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faHandshake} className="me-2 text-primary" />Vendor / BAA Register</h2>
                    <p className="text-muted mb-0">Third parties processing data on the platform's behalf</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Register Vendor</button>
            </div>

            {unprotected > 0 && (
                <div className="alert alert-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>{unprotected} vendor(s) process PHI without a signed data-processing agreement.</strong>
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : vendors.length === 0 ? (
                        <div className="text-center py-5 text-muted">No vendors registered yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Vendor</th><th>PHI</th><th>Agreement</th><th>Signed</th><th>Expires</th><th>Risk</th><th></th></tr></thead>
                            <tbody>
                                {vendors.map(v => (
                                    <tr key={v.id} className={v.processes_phi && v.agreement_status !== 'signed' ? 'table-danger' : ''}>
                                        <td><strong>{v.name}</strong><div className="small text-muted">{v.service_provided}</div></td>
                                        <td>{v.processes_phi ? <span className="badge bg-danger">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                                        <td style={{ minWidth: 130 }}>
                                            <select className="form-select form-select-sm" value={v.agreement_status} onChange={(e) => patchVendor(v, { agreement_status: e.target.value })}>
                                                {AGREEMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </td>
                                        <td className="small">{v.agreement_signed_date || '—'}</td>
                                        <td className="small">{v.agreement_expiry_date || '—'}</td>
                                        <td><span className={`badge bg-${RISK_COLORS[v.risk_rating]}`}>{v.risk_rating}</span></td>
                                        <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeVendor(v)}><FontAwesomeIcon icon={faTrash} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Register Vendor</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Vendor name</label>
                                    <input className="form-control" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Service provided</label>
                                    <input className="form-control" value={form.service_provided} onChange={(e) => setForm(f => ({ ...f, service_provided: e.target.value }))} />
                                </div>
                                <div className="form-check mb-3">
                                    <input className="form-check-input" type="checkbox" id="phi" checked={form.processes_phi}
                                        onChange={(e) => setForm(f => ({ ...f, processes_phi: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor="phi">This vendor processes PHI</label>
                                    <div className="form-text">If checked, a signed agreement becomes a compliance requirement.</div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Agreement status</label>
                                        <select className="form-select" value={form.agreement_status} onChange={(e) => setForm(f => ({ ...f, agreement_status: e.target.value }))}>
                                            {AGREEMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Risk rating</label>
                                        <select className="form-select" value={form.risk_rating} onChange={(e) => setForm(f => ({ ...f, risk_rating: e.target.value }))}>
                                            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label className="form-label">Contact email</label>
                                    <input type="email" className="form-control" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Register</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default VendorRegister;
